import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { Card, CardContent } from "@/components/ui/card";
import { Truck, AlertCircle } from "lucide-react";
import { getPublicSiteRules } from "@/lib/api";
import { supabase } from "@/lib/supabaseClient";
import SectionRenderer from "@/components/SectionRenderer";
import { useTenantBranding, TenantLogo } from "@/components/TenantBrand";

export default function SiteRulesPublic() {
  const { siteId } = useParams();
  const [site, setSite] = useState(null);
  const [data, setData] = useState({ rules: null, sections: [] });
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState(null);
  const brand = useTenantBranding({ site_id: siteId });

  useEffect(() => {
    let cancelled = false;
    (async () => {
      const [{ data: s, error: se }, r] = await Promise.all([
        supabase.from("lbs_sites").select("id,name,code,city,state,address_line1,contact_phone").eq("id", siteId).maybeSingle(),
        getPublicSiteRules(siteId),
      ]);
      if (cancelled) return;
      // Only treat as error if BOTH site lookup and rules failed.
      // Anon RLS may legitimately hide the site row while still exposing
      // rules + sections — degrade gracefully in that case.
      if (se && r.error) setErr((se || r.error).message);
      setSite(s);
      setData(r);
      setLoading(false);
    })();
    return () => { cancelled = true; };
  }, [siteId]);

  return (
    <div className="min-h-screen bg-slate-50 py-8 px-4">
      <div className="max-w-3xl mx-auto" data-testid="site-rules-public">
        <TenantLogo brand={brand} className="mb-4" />
        <div className="flex items-center gap-2 text-sm text-slate-500 mb-4">
          <span className="font-semibold text-slate-900">timeslot.au</span>
          <span>·</span>
          <span>Site rules</span>
        </div>
        <Card>
          <CardContent className="p-6 space-y-8">
            {loading ? (
              <p className="text-slate-500 text-sm">Loading…</p>
            ) : err ? (
              <div className="text-center py-10">
                <AlertCircle className="h-10 w-10 text-rose-500 mx-auto mb-2" />
                <p className="text-sm text-slate-600">{err}</p>
              </div>
            ) : !site && data.sections.length === 0 ? (
              <p className="text-sm text-slate-600">Site not found.</p>
            ) : (
              <>
                {data.rules?.banner_image && (
                  <img
                    src={data.rules.banner_image}
                    alt={`${site?.name || "Site"} banner`}
                    className="w-full h-48 object-cover rounded-md -mt-2"
                    data-testid="rules-public-banner"
                  />
                )}
                <header>
                  <h1 className="text-2xl font-semibold flex items-center gap-2">
                    <Truck className="h-6 w-6" /> {site?.name || "Site rules"}
                  </h1>
                  {site && (site.address_line1 || site.city) && (
                    <p className="text-sm text-slate-500 mt-1">
                      {[site.address_line1, site.city, site.state].filter(Boolean).join(", ")}
                    </p>
                  )}
                </header>
                {data.rules?.intro_markdown && (
                  <div className="text-sm whitespace-pre-wrap text-slate-700">
                    {data.rules.intro_markdown}
                  </div>
                )}
                {data.sections.map((s) => (
                  <SectionRenderer key={s.id} section={s} />
                ))}
                {data.sections.length === 0 && (
                  <p className="text-slate-400 text-sm">No site rules published yet.</p>
                )}
              </>
            )}
          </CardContent>
        </Card>
        <p className="text-[11px] text-center text-slate-400 mt-3">
          Powered by timeslot.au · Last updated {data.rules?.updated_at ? new Date(data.rules.updated_at).toLocaleDateString("en-AU") : "—"}
        </p>
      </div>
    </div>
  );
}
